import React, { createContext, useContext, ReactNode, useState, useEffect, useRef } from 'react';
import { useForm, UseFormReturn } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { ProductDetailsSchema, ProductFormData, ProductSubmissionSchema, ProductSubmissionData } from '../schemas/productSchemas';

// Deep equality comparison function to replace JSON.stringify
const deepEqual = (obj1: any, obj2: any): boolean => {
  if (obj1 === obj2) return true;
  
  if (obj1 == null || obj2 == null) return obj1 === obj2;
  
  if (typeof obj1 !== typeof obj2) return false;
  
  if (typeof obj1 !== 'object') return obj1 === obj2;
  
  if (Array.isArray(obj1) !== Array.isArray(obj2)) return false;
  
  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);
  
  if (keys1.length !== keys2.length) return false;
  
  for (let key of keys1) {
    if (!keys2.includes(key)) return false;
    if (!deepEqual(obj1[key], obj2[key])) return false;
  }
  
  return true;
};

// Helper function to find differences between two objects
const findDifferences = (obj1: any, obj2: any, path: string = ''): string[] => {
  const differences: string[] = [];
  
  if (obj1 === obj2) return differences;
  
  if (typeof obj1 !== typeof obj2) {
    differences.push(`${path}: type mismatch (${typeof obj1} vs ${typeof obj2})`);
    return differences;
  }
  
  if (obj1 == null || obj2 == null) {
    differences.push(`${path}: null/undefined mismatch (${obj1} vs ${obj2})`);
    return differences;
  }
  
  if (typeof obj1 !== 'object') {
    differences.push(`${path}: value mismatch (${obj1} vs ${obj2})`);
    return differences;
  }
  
  if (Array.isArray(obj1) !== Array.isArray(obj2)) {
    differences.push(`${path}: array type mismatch`);
    return differences;
  }
  
  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);
  
  // Check for missing keys
  for (let key of keys1) {
    if (!keys2.includes(key)) {
      differences.push(`${path}.${key}: missing in second object`);
    }
  }
  
  for (let key of keys2) {
    if (!keys1.includes(key)) {
      differences.push(`${path}.${key}: missing in first object`);
    }
  }
  
  // Check for value differences
  for (let key of keys1) {
    if (keys2.includes(key)) {
      const newPath = path ? `${path}.${key}` : key;
      differences.push(...findDifferences(obj1[key], obj2[key], newPath));
    }
  }
  
  return differences;
};

interface ProductFormContextType {
  form: UseFormReturn<ProductFormData>;
  isDirty: boolean;
  isValid: boolean;
  isSubmitting: boolean;
  resetForm: () => void;
  resetFormWithNewValues: (values: Partial<ProductFormData>) => void;
  updateInitialValues: (values: Partial<ProductFormData>) => void;
  submitForm: () => Promise<void>;
  onError?: (error: Error) => void;
}

const ProductFormContext = createContext<ProductFormContextType | undefined>(undefined);

// Props for the provider component
interface ProductFormProviderProps {
  children: ReactNode;
  defaultValues?: Partial<ProductFormData>;
  onSubmit?: (data: ProductSubmissionData, resetFormWithNewValues?: (values: Partial<ProductFormData>) => void) => Promise<void>;
  onError?: (error: Error) => void;
}

// Provider component
export const ProductFormProvider: React.FC<ProductFormProviderProps> = ({
  children,
  defaultValues = {
    productProfile: {},
    stockControl: {},
    replenishment: {},
    glAccounts: {},
    extraDetails: {},
    auditProfile: {},
  },
  onSubmit,
  onError,
}) => {
  // Custom dirty state tracking
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [initialValues, setInitialValues] = useState(defaultValues);
  const isResettingRef = useRef(false);

  // Initialize React Hook Form with Zod validation
  const form = useForm<ProductFormData>({
    resolver: zodResolver(ProductDetailsSchema),
    defaultValues,
    mode: 'onChange', // Validate on change for better UX
    reValidateMode: 'onChange',
  });

  const {
    formState: { isValid, isSubmitting, errors },
    reset,
    handleSubmit,
    watch,
  } = form;

  // Watch all form values to detect changes
  const watchedValues = watch();

  // Track dirty state by comparing current values with initial values using deep equality
  useEffect(() => {
    // Skip dirty check if we're in the middle of resetting
    if (isResettingRef.current) {
      return;
    }

    const hasChanges = !deepEqual(watchedValues, initialValues);
    
    // Debug logging to help identify differences
    if (hasChanges !== isFormDirty) {
      console.log('Form dirty state changed:', hasChanges);
      if (hasChanges) {
        const differences = findDifferences(watchedValues, initialValues);
        console.log('Differences found:', differences);
        console.log('Current values:', watchedValues);
        console.log('Initial values:', initialValues);
      }
    }
    
    setIsFormDirty(hasChanges);
  }, [watchedValues, initialValues, isFormDirty]);

  // Debug validation errors
  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      console.log('Form Validation Errors:', errors);
    }
  }, [errors]);

  // Reset form to default values
  const resetForm = () => {
    isResettingRef.current = true;
    reset(defaultValues);
    setInitialValues(defaultValues);
    setIsFormDirty(false);
    // Allow dirty checking to resume after a brief delay
    setTimeout(() => {
      isResettingRef.current = false;
    }, 100);
  };

  // Reset form with new values after successful submission
  const resetFormWithNewValues = (newValues: Partial<ProductFormData>) => {
    isResettingRef.current = true;
    reset(newValues);
    setInitialValues(newValues);
    setIsFormDirty(false);
    // Allow dirty checking to resume after a brief delay
    setTimeout(() => {
      isResettingRef.current = false;
    }, 100);
  };

  // Handle form submission
  const submitForm = async () => {
    if (onSubmit) {
      await handleSubmit(async (data) => {
        // Validate with submission schema for stricter validation
        const submissionData = ProductSubmissionSchema.parse(data);
        console.log('submitForm', submissionData);
        
        await onSubmit(submissionData, resetFormWithNewValues);
        // Reset dirty state after successful submission
        isResettingRef.current = true;
        setIsFormDirty(false);
        setInitialValues(data);
        // Allow dirty checking to resume after a brief delay
        setTimeout(() => {
          isResettingRef.current = false;
        }, 100);
      })();
    }
  };

  const contextValue: ProductFormContextType = {
    form,
    isDirty: isFormDirty,
    isValid,
    isSubmitting,
    resetForm,
    resetFormWithNewValues,
    updateInitialValues: (values: Partial<ProductFormData>) => {
      isResettingRef.current = true;
      setInitialValues(values);
      setIsFormDirty(false);
      // Allow dirty checking to resume after a brief delay
      setTimeout(() => {
        isResettingRef.current = false;
      }, 100);
    },
    submitForm,
    onError,
  };

  return (
    <ProductFormContext.Provider value={contextValue}>
      {children}
    </ProductFormContext.Provider>
  );
};

// Custom hook to use the form context
export const useProductForm = (): ProductFormContextType => {
  const context = useContext(ProductFormContext);
  if (context === undefined) {
    throw new Error('useProductForm must be used within a ProductFormProvider');
  }
  return context;
};

// Helper hook for individual form sections
export const useFormSection = <T extends keyof ProductFormData>(
  section: T
) => {
  const { form } = useProductForm();
  const { register, watch, setValue, formState: { errors } } = form;

  return {
    register: (field: string) => register(`${section}.${field}` as any),
    watch: (field: string) => watch(`${section}.${field}` as any),
    setValue: (field: string, value: any) => setValue(`${section}.${field}` as any, value),
    getError: (field: string) => {
      const sectionErrors = errors[section] as any;
      return sectionErrors?.[field];
    },
    sectionErrors: errors[section] as any,
  };
};

export default ProductFormContext;